import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Estimation, EstimationData, EstimationFilters, EstimationFromApi, EstimationResponse, FullEstimation, FullEstimationFromApi } from '../shared/models/estimation.model';
import { handleApiResponse } from '../utils/common-methods';
import { AlertMessageService } from './alert-message.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class EstimationService {
  private headers = new HttpHeaders();
  
  constructor(private http: HttpClient, private userService: UserService, private alertService: AlertMessageService) {}

  public estimationTabs = [
    'Todas',
    'Iniciado',
    'Por revisar',
    'Por autorizar',
    'Por enviar a pagos',
    'Enviado a pagos',
    'En proceso de pago',
    'Pagada',
    'Rechazada',
    'Cancelada',
  ];

  // getEstimation(id: string): Observable<Estimation> {
  //   return of(estimation).pipe(delay(500));
  // }

  getLatestToAuthorizeEstimations(
    projectId: number | 'all',
    userId: number
  ): Observable<Estimation[]> {
    const headers = this.userService.headers;
    const URL = `${environment.baseURL}/dashboards/work-progress-statement_table/${projectId}?page=1&limit=30`;

    return this.http.get<EstimationResponse>(URL, { headers }).pipe(
      map((response: EstimationResponse) => {
        return (handleApiResponse(response, this.alertService) as EstimationData)
          .rows.map((estimation: EstimationFromApi) => {
            return this.mapEstimationFromApiToEstimation(estimation);
          });
      })
    );
  }

  getEstimationFilters(projectId: number | 'all'): Observable<any> {
    const headers = this.userService.headers;
    const URL = `${environment.baseURL}/filter-info/work-progress-statement`;
    return this.http.get<EstimationResponse>(URL, { headers }).pipe(
      map((response: EstimationResponse) => {
        return handleApiResponse(response, this.alertService) as EstimationFilters ?? {};
        // return {};
      }),
      catchError((error) => {
        console.error('Error fetching estimation filters:', error);
        this.alertService.updateAlert(error.error!);
        this.alertService.changeErrorState(true);
        return of({} as EstimationFilters);
      })
    );
  }

  getEstimationsListByProjectId(
    projectId: number | 'all',
    page: number,
    limit: number,
    order: string,
    filters: string[]
  ) {
    const headers = this.userService.headers;
    const URL = `${environment.baseURL}/dashboards/work-progress-statement_table/${projectId}?page=${page}&limit=${limit}`
    +`${
      order === undefined || order === null || order === ''
        ? ''
        : '&order=' + order
    }` +
      `${
        filters.length > 0
          ? `${filters.map((x) => `&filters=${x}`).join('&')}`
          : ''
      }`;
    return this.http.get<EstimationResponse>(URL, { headers }).pipe(
      map((response: EstimationResponse) => {
        return handleApiResponse(response, this.alertService) as EstimationData ?? {};
      }),
      catchError((error) => {
        console.error('Error fetching requisitions by project:', error);
        this.alertService.updateAlert(error.error!);
        this.alertService.changeErrorState(true);
        return of({} as EstimationData);
      })
    );
  }

  getEstimationById(estimationId: string | null): Observable<FullEstimationFromApi> {
    const headers = this.userService.headers;
    const URL = `${environment.baseURL}/work-progress-statement/${estimationId}?wpsType=wpsFull`;
    return this.http.get<EstimationResponse>(URL, { headers }).pipe(
      map((response: EstimationResponse) => {
        return handleApiResponse(response, this.alertService) as FullEstimationFromApi ?? {};
      }),
      catchError((error) => {
        console.error('Error fetching estimation by ID:', error);
        this.alertService.updateAlert(error.error!);
        this.alertService.changeErrorState(true);
        return of({} as FullEstimationFromApi);
      })
    );
  }

  createEstimation(estimation: Partial<Estimation>) {
    const headers = this.userService.headers;
    const URL = `${environment.baseURL}/work-progress-statement`;
    return this.http.post<EstimationResponse>(URL, estimation, { headers }).pipe(
      map((response: EstimationResponse) => {
        return handleApiResponse(response, this.alertService) as Estimation ?? {};
      }),
      catchError((error) => {
        console.error('Error creating estimation:', error);
        this.alertService.updateAlert(error.error!);
        this.alertService.changeErrorState(true);
        return of({} as Estimation);
      })
    );
  }

  updateEstimation(estimation: Partial<Estimation>) {
    const headers = this.userService.headers;
    const URL = `${environment.baseURL}/work-progress-statement/${estimation.estimationId}`;
    return this.http.put<EstimationResponse>(URL, estimation, { headers }).pipe(
      map((response: EstimationResponse) => {
        return handleApiResponse(response, this.alertService) as Estimation ?? {};
      }),
      catchError((error) => {
        console.error('Error updating estimation:', error);
        this.alertService.updateAlert(error.error!);
        this.alertService.changeErrorState(true);
        return of({} as Estimation);
      })
    );
  }

  deleteEstimation(estimationId: number): Observable<boolean> {
    const headers = this.userService.headers;
    const URL = `${environment.baseURL}/work-progress-statement/${estimationId}`;
    return this.http.delete<EstimationResponse>(URL, { headers }).pipe(
      map((response: EstimationResponse) => {
        return handleApiResponse(response, this.alertService) as boolean ?? false;
      }),
      catchError((error) => {
        console.error('Error deleting estimation:', error);
        this.alertService.updateAlert(error.error!);
        this.alertService.changeErrorState(true);
        return of(false);
      })
    );
  }

  mapEstimationFromApiToEstimation(estimation: EstimationFromApi): Estimation {
    return ({
      estimationId: estimation.id,
      estimationKey: estimation.key,
      project: estimation.project,
      estimationFolio: estimation.folio,
      estimationDescription: estimation.description,
      estimationApplicationDate: new Date(estimation.statementDate),
      estimationNotes: estimation.notes,
      totalCost: estimation.totalCost,
      paymentMethod: estimation.paymentMethod,
      periodStart: estimation.periodStart,
      periodEnd: estimation.periodEnd,
      workPrepayment: estimation.workPrepayment,
      workPrepaymentRefund: estimation.workPrepaymentRefund,
      estimationIsHighlighted: estimation.isFeatured,
      estimationPriority: estimation.priorityInfo,
      estimationStatus: estimation.statusInfo,
      estimationProgress:estimation.currentProgressInfo,
      estimationType: estimation.type_Info,
      supplier: estimation.supplier,
      userWhoRequests: estimation.createdBy,
      userWhoAuthorizes: estimation.authorizedBy,
      userWhoReviews: estimation.reviewedBy,
      company: estimation.company,
    }) as Estimation;
  }

  mapFullEstimationFromApiToFullEstimation(estimation: FullEstimationFromApi): FullEstimation {
    return {
      company: {
        key: estimation.Company.clave,
        ID: estimation.Company.id,
        businessName: estimation.Company.razonSocial,
      },
      createdBy: {
        lastName: estimation.CreatedBy.APELLIDOS,
        authorizeEstimations: estimation.CreatedBy.AUTORIZAEST,
        authorizeRequisitions: estimation.CreatedBy.AUTORIZAREQS,
        isAdministrator: estimation.CreatedBy.ESADMINISTRADOR,
        image: estimation.CreatedBy.Image,
        firstName: estimation.CreatedBy.NOMBRES,
        fullName: estimation.CreatedBy.NombreCompleto,
        profileIDS: estimation.CreatedBy.PERFILIDS,
        permissions: estimation.CreatedBy.Permisos,
        role: estimation.CreatedBy.Role,
        user: estimation.CreatedBy.USUARIO,
        userID: estimation.CreatedBy.USUARIOID,
        viewAmounts: estimation.CreatedBy.VERIMPORTES,
      },
      ID: estimation.ID,
      prepayment: estimation.Prepayment,
      project: {
        additionalData: estimation.Project.DatosAdicionales,
        projectCompany: estimation.Project.EmpresaDelProyecto,
        key: estimation.Project.clave,
        description: estimation.Project.descripcion,
        address: estimation.Project.direccion,
        companyID: estimation.Project.empresaId,
        status: estimation.Project.estado,
        endDate: estimation.Project.fechaFin,
        startDate: estimation.Project.fechaIni,
        ID: estimation.Project.id,
        controlAmount: estimation.Project.importeDeControl,
        basebudget: estimation.Project.presupuestoBase,
      },
      supplier: {
        lastName: estimation.Supplier.apellidos,
        key: estimation.Supplier.clave,
        email: estimation.Supplier.email,
        paymentMethod: estimation.Supplier.forma_de_pago,
        ID: estimation.Supplier.id,
        firstName: estimation.Supplier.nombres,
        businessName: estimation.Supplier.razon_social,
      },
      accountID: estimation.accountId,
      advancePayment: estimation.advancePayment,
      advanceRetention: estimation.advanceRetention,
      canceledByID: estimation.canceledByID,
      cancellationReason: estimation.cancellationReason,
      cancelledAt: estimation.cancelledAt,
      companyID: estimation.companyID,
      createdAt: estimation.createdAt,
      createdByID: estimation.createdByID,
      currentProgress: estimation.currentProgress,
      currentProgressLabel: estimation.currentProgressLabel,
      description: estimation.description,
      folioNumber: estimation.folioNumber,
      include: estimation.include,
      invoiced: estimation.invoiced,
      isAnAdvancePayment: estimation.isAnAdvancePayment,
      isCancelled: estimation.isCancelled,
      isFeatured: estimation.isFeatured,
      isRejected: estimation.isRejected,
      isSentToTreasury: estimation.isSentToTreasury,
      netAmount: estimation.netAmount,
      notes: estimation.notes,
      paid: estimation.paid,
      paymentMethod: estimation.paymentMethod,
      periodEndDate: estimation.periodEndDate,
      periodStartDate: estimation.periodStartDate,
      priority: estimation.priority,
      priorityLabel: estimation.priorityLabel,
      projectID: estimation.projectID,
      projectName: estimation.projectName,
      rejectedAt: estimation.rejectedAt,
      rejectedByID: estimation.rejectedByID,
      rejectionReason: estimation.rejectionReason,
      reviewed: estimation.reviewed,
      sFolio: estimation.sFolio,
      statementDate: estimation.statementDate,
      status: estimation.status,
      statusLabel: estimation.statusLabel,
      subProjectID: estimation.subProjectID,
      subTotal: estimation.subTotal,
      subcontractAgreementID: estimation.subcontractAgreementID,
      supplierID: estimation.supplierID,
      type: estimation.type,
      typeLabel: estimation.typeLabel,
      updatedAt: estimation.updatedAt,
      vatAmount: estimation.vatAmount,
      vatPercentage: estimation.vatPercentage,
      warrantyRefund: estimation.warrantyRefund,
      warrantyRetention: estimation.warrantyRetention,
      warrantyRetentionPercentage: estimation.warrantyRetentionPercentage,
      workPrepayment: estimation.workPrepayment,
      workPrepaymentRefund: estimation.workPrepaymentRefund,
    };
  }

  /**
   * Fetches the estimation items for a specific estimation.
   * Currently returns mock data for demonstration purposes.
   *
   * @param {number} estimationId - The unique identifier of the estimation to fetch items for.
   * @param {boolean} grouped - Whether to return grouped items or not.
   * @return {Observable<any>} An observable that emits the estimation items.
   */
  public getEstimationItems(estimationId: number, grouped?: boolean): Observable<any> {
    // Mock data similar to requisition items structure
    const mockEstimationItems = {
      Nodes: [
        {
          id: 1,
          context: 'EstimationItem',
          key: 'CEM-001',
          parentKey: '',
          name: "Cemento Portland Tipo I",
          description: 'Cemento Portland Tipo I',
          type: 'Material',
          order: 1,
          WBSResourceNode: {
            familyName: "",
            unit: "SAC",
            supplierName: "",
            currentInputQuantity: 0,
            currentInputPercentage: 0,
            unitCost: 0,
            budgetedQuantity: 0.568368,
            quantity: 0,
            consumedQuantity: 0.568368,
            remainingQuantity: 0,
            previouslyConsumedQuantity: 0.568368,
            totalConsumedQuantity: 0.568368,
            totalRemainingQuantity: 100,
            currentConsumedQuantity: 0.568368,
            currentRemainingQuantity: 0,
            unitPrice: 450.75,
            assignedPercentage: 15.0,
            totalAmount: 6761.25,
            fileCount: 3
          }
        },
        {
          id: 2,
          context: 'EstimationItem',
          key: 'CEM-002',
          parentKey: 'CEM-001',
          name: "Cemento Portland Tipo I",
          description: 'Acero de refuerzo corrugado #4',
          type: 'Material',
          order: 1,
          WBSResourceNode: {
            familyName: "",
            unit: "SAC",
            supplierName: "",
            currentInputQuantity: 0,
            currentInputPercentage: 26,
            unitCost: 0,
            budgetedQuantity: 0.568368,
            quantity: 0,
            consumedQuantity: 0.568368,
            remainingQuantity: 0,
            previouslyConsumedQuantity: 0.568368,
            totalConsumedQuantity: 0.568368,
            totalRemainingQuantity: 20.77,
            currentConsumedQuantity: 0.568368,
            currentRemainingQuantity: 0,
            unitPrice: 1250.00,
            assignedPercentage: 17.0,
            totalAmount: 10625.00,
            fileCount: 2
          }
        },
        {
          id: 3,
          context: 'EstimationItem',
          key: 'CEM-003',
          parentKey: 'CEM-001',
          name: "Cemento Portland Tipo I",
          description: 'Excavadora CAT 320D',
          type: 'Machinery',
          order: 1,
          WBSResourceNode: {
            familyName: "",
            unit: "SAC",
            supplierName: "",
            currentInputQuantity: 0,
            currentInputPercentage: 70,
            unitCost: 0,
            budgetedQuantity: 0.568368,
            quantity: 0,
            consumedQuantity: 0.568368,
            remainingQuantity: 0,
            previouslyConsumedQuantity: 0.568368,
            totalConsumedQuantity: 0.578368,
            totalRemainingQuantity: 80.00,
            currentConsumedQuantity: 0.568368,
            currentRemainingQuantity: 0,
            unitPrice: 350.00,
            assignedPercentage: 20.0,
            totalAmount: 14000.00,
            fileCount: 5
          }
        },
        {
          id: 4,
          context: 'EstimationItem',
          key: 'CEM-004',
          parentKey: 'CEM-001',
          name: "Cemento Portland Tipo I",
          description: 'Concreto f\'c=250 kg/cm²',
          type: 'Material',
          order: 1,
          WBSResourceNode: {
            familyName: "",
            unit: "SAC",
            supplierName: "",
            currentInputQuantity: 0,
            currentInputPercentage: 14,
            unitCost: 0,
            budgetedQuantity: 0.568368,
            quantity: 0,
            consumedQuantity: 0.568368,
            remainingQuantity: 0,
            previouslyConsumedQuantity: 0.568368,
            totalConsumedQuantity: 0.568368,
            totalRemainingQuantity: 50.00,
            currentConsumedQuantity: 0.568368,
            currentRemainingQuantity: 0,
            unitPrice: 2800.50,
            assignedPercentage: 33.33,
            totalAmount: 70012.50,
            fileCount: 1
          }
        },
        {
          id: 5,
          context: 'EstimationItem',
          key: 'CEM-001',
          parentKey: 'cue_613',
          name: "Cemento Portland Tipo I",
          description: 'Albañil especializado',
          type: 'Machinery',
          order: 1,
          WBSResourceNode: {
            familyName: "",
            unit: "SAC",
            supplierName: "",
            currentInputQuantity: 0,
            currentInputPercentage: 90,
            unitCost: 0,
            budgetedQuantity: 0.568368,
            quantity: 0,
            consumedQuantity: 0.568368,
            remainingQuantity: 0,
            previouslyConsumedQuantity: 0.568368,
            totalConsumedQuantity: 0.598368,
            totalRemainingQuantity: 48.00,
            currentConsumedQuantity: 0.568368,
            currentRemainingQuantity: 0,
            unitPrice: 650.00,
            assignedPercentage: 20.0,
            totalAmount: 15600.00,
            fileCount: 0
          }
        }
      ]
    };

    // Simulate API delay
    return of(mockEstimationItems).pipe(
      map((response) => response),
      catchError((error) => {
        console.error('Error fetching estimation items:', error);
        this.alertService.updateAlert(error.error!);
        this.alertService.changeErrorState(true);
        return of({ Nodes: [] });
      })
    );
  }

  /**
   * Retrieves estimation items in expanded/tree view format
   * @param {number} estimationId - The unique identifier of the estimation to fetch items for.
   * @return {Observable<any>} An observable that emits the estimation items in tree structure.
   */
  public getEstimationItemsExpanded(estimationId: number): Observable<any> {
    // Mock data with realistic hierarchical structure based on real API format
    const mockEstimationItemsExpanded = {
      items: [
        // Component/Activity items (parent nodes)
        {
          itemId: 1001,
          parentId: null,
          resourceId: null,
          resourceName: "O001: PRELIMINARES",
          resourceDescription: "Trabajos preliminares de construcción",
          resourceUnit: "",
          componentId: 614,
          componentName: "O001: PRELIMINARES",
          componentType: "Activity",
          quantity: 0,
          notes: null,
          productionUnitId: null,
          productionUnitName: null,
          componentResourceId: null,
          extendedItemData: {
            familyName: "Preliminares",
            unit: "",
            currentInputQuantity: 0,
            currentInputPercentage: 0,
            unitCost: 0,
            budgetedQuantity: 0,
            previouslyConsumedQuantity: 0,
            totalConsumedQuantity: 0,
            totalRemainingQuantity: 0,
            currentConsumedQuantity: 0,
            currentRemainingQuantity: 0,
            assignedPercentage: 45.5,
            totalAmount: 125378.75
          }
        },
        {
          itemId: 1002,
          parentId: null,
          resourceId: null,
          resourceName: "O002: CIMENTACION",
          resourceDescription: "Trabajos de cimentación y estructura",
          resourceUnit: "",
          componentId: 615,
          componentName: "O002: CIMENTACION",
          componentType: "Activity",
          quantity: 0,
          notes: null,
          productionUnitId: null,
          productionUnitName: null,
          componentResourceId: null,
          extendedItemData: {
            familyName: "Cimentación",
            unit: "",
            currentInputQuantity: 0,
            currentInputPercentage: 0,
            unitCost: 0,
            budgetedQuantity: 0,
            previouslyConsumedQuantity: 0,
            totalConsumedQuantity: 0,
            totalRemainingQuantity: 0,
            currentConsumedQuantity: 0,
            currentRemainingQuantity: 0,
            assignedPercentage: 62.8,
            totalAmount: 189750.00
          }
        },
        {
          itemId: 1003,
          parentId: null,
          resourceId: null,
          resourceName: "O003: ESTRUCTURA",
          resourceDescription: "Trabajos de estructura metálica y concreto",
          resourceUnit: "",
          componentId: 616,
          componentName: "O003: ESTRUCTURA",
          componentType: "Activity",
          quantity: 0,
          notes: null,
          productionUnitId: null,
          productionUnitName: null,
          componentResourceId: null,
          extendedItemData: {
            familyName: "Estructura",
            unit: "",
            currentInputQuantity: 0,
            currentInputPercentage: 0,
            unitCost: 0,
            budgetedQuantity: 0,
            previouslyConsumedQuantity: 0,
            totalConsumedQuantity: 0,
            totalRemainingQuantity: 0,
            currentConsumedQuantity: 0,
            currentRemainingQuantity: 0,
            assignedPercentage: 78.2,
            totalAmount: 298650.00
          }
        },

        // Material items for PRELIMINARES (child nodes)
        {
          itemId: 5178,
          parentId: 1001,
          resourceId: 8785,
          resourceName: "1ACER001-8785",
          resourceDescription: "VARILLA DE 3/8\"",
          resourceUnit: "PZA",
          componentId: 614,
          componentName: "O001: PRELIMINARES",
          componentType: "Material",
          quantity: 12,
          notes: null,
          productionUnitId: 73,
          productionUnitName: "Casa 101",
          componentResourceId: 9867,
          extendedItemData: {
            familyName: "Acero",
            unit: "PZA",
            currentInputQuantity: 12,
            currentInputPercentage: 14400.0,
            unitCost: 45.50,
            budgetedQuantity: 0.083334,
            previouslyConsumedQuantity: 0,
            totalConsumedQuantity: 12,
            totalRemainingQuantity: -11.916666,
            currentConsumedQuantity: 12,
            currentRemainingQuantity: -11.916666,
            assignedPercentage: 1440000.0,
            totalAmount: 546.00
          }
        },
        {
          itemId: 5179,
          parentId: 1001,
          resourceId: 8786,
          resourceName: "1SANI015",
          resourceDescription: "TUBO SANITARIO DE 4\"",
          resourceUnit: "ML",
          componentId: 614,
          componentName: "O001: PRELIMINARES",
          componentType: "Material",
          quantity: 25,
          notes: "Tubo para instalaciones sanitarias - Casa 101",
          productionUnitId: 73,
          productionUnitName: "Casa 101",
          componentResourceId: 9868,
          extendedItemData: {
            familyName: "Sanitarios",
            unit: "ML",
            currentInputQuantity: 25,
            currentInputPercentage: 200.0,
            unitCost: 85.75,
            budgetedQuantity: 12.50,
            previouslyConsumedQuantity: 0,
            totalConsumedQuantity: 25,
            totalRemainingQuantity: -12.50,
            currentConsumedQuantity: 25,
            currentRemainingQuantity: -12.50,
            assignedPercentage: 200.0,
            totalAmount: 2143.75
          }
        },
        {
          itemId: 5180,
          parentId: 1001,
          resourceId: 8787,
          resourceName: "1AGLU001-8787",
          resourceDescription: "CEMENTO GRIS APASCO SACO DE 50 KG",
          resourceUnit: "SAC",
          componentId: 614,
          componentName: "O001: PRELIMINARES",
          componentType: "Material",
          quantity: 45,
          notes: null,
          productionUnitId: 73,
          productionUnitName: "Casa 101",
          componentResourceId: 9869,
          extendedItemData: {
            familyName: "Aglutinantes",
            unit: "SAC",
            currentInputQuantity: 45,
            currentInputPercentage: 75.0,
            unitCost: 285.00,
            budgetedQuantity: 60.0,
            previouslyConsumedQuantity: 15,
            totalConsumedQuantity: 60,
            totalRemainingQuantity: 0,
            currentConsumedQuantity: 45,
            currentRemainingQuantity: 0,
            assignedPercentage: 75.0,
            totalAmount: 12825.00
          }
        },
        {
          itemId: 5181,
          parentId: 1001,
          resourceId: 8788,
          resourceName: "1AGRE001",
          resourceDescription: "POLVO DE PIEDRA",
          resourceUnit: "M3",
          componentId: 614,
          componentName: "O001: PRELIMINARES",
          componentType: "Material",
          quantity: 1.5,
          notes: null,
          productionUnitId: 73,
          productionUnitName: "Casa 101",
          componentResourceId: 9870,
          extendedItemData: {
            familyName: "Agregados",
            unit: "M3",
            currentInputQuantity: 1.5,
            currentInputPercentage: 73.16,
            unitCost: 450.00,
            budgetedQuantity: 2.050443,
            previouslyConsumedQuantity: 0,
            totalConsumedQuantity: 1.5,
            totalRemainingQuantity: 0.550443,
            currentConsumedQuantity: 1.5,
            currentRemainingQuantity: 0.550443,
            assignedPercentage: 73.16,
            totalAmount: 675.00
          }
        },

        // Labor items for PRELIMINARES
        {
          itemId: 5185,
          parentId: 1001,
          resourceId: 8810,
          resourceName: "1MANO005",
          resourceDescription: "PEON GENERAL",
          resourceUnit: "JOR",
          componentId: 614,
          componentName: "O001: PRELIMINARES",
          componentType: "Labor",
          quantity: 8,
          notes: "Mano de obra para trabajos preliminares",
          productionUnitId: 73,
          productionUnitName: "Casa 101",
          componentResourceId: 9880,
          extendedItemData: {
            familyName: "Mano de Obra",
            unit: "JOR",
            currentInputQuantity: 8,
            currentInputPercentage: 40.0,
            unitCost: 450.00,
            budgetedQuantity: 20.0,
            previouslyConsumedQuantity: 4.0,
            totalConsumedQuantity: 12.0,
            totalRemainingQuantity: 8.0,
            currentConsumedQuantity: 8,
            currentRemainingQuantity: 4.0,
            assignedPercentage: 40.0,
            totalAmount: 3600.00
          }
        },

        // Material items for CIMENTACION (child nodes)
        {
          itemId: 5182,
          parentId: 1002,
          resourceId: 8802,
          resourceName: "1ACER002",
          resourceDescription: "ACERO ESTRUCTURAL A-36",
          resourceUnit: "TON",
          componentId: 615,
          componentName: "O002: CIMENTACION",
          componentType: "Material",
          quantity: 12.5,
          notes: "Acero para estructura principal",
          productionUnitId: 73,
          productionUnitName: "Casa 101",
          componentResourceId: 9877,
          extendedItemData: {
            familyName: "Acero",
            unit: "TON",
            currentInputQuantity: 12.5,
            currentInputPercentage: 25.0,
            unitCost: 1890.00,
            budgetedQuantity: 50.0,
            previouslyConsumedQuantity: 12.5,
            totalConsumedQuantity: 25.0,
            totalRemainingQuantity: 25.0,
            currentConsumedQuantity: 12.5,
            currentRemainingQuantity: 12.5,
            assignedPercentage: 25.0,
            totalAmount: 23625.00
          }
        },
        {
          itemId: 5186,
          parentId: 1002,
          resourceId: 8803,
          resourceName: "1CONC001",
          resourceDescription: "CONCRETO F'C=250 KG/CM2",
          resourceUnit: "M3",
          componentId: 615,
          componentName: "O002: CIMENTACION",
          componentType: "Material",
          quantity: 85.0,
          notes: null,
          productionUnitId: 73,
          productionUnitName: "Casa 101",
          componentResourceId: 9878,
          extendedItemData: {
            familyName: "Concreto",
            unit: "M3",
            currentInputQuantity: 85.0,
            currentInputPercentage: 85.0,
            unitCost: 2800.50,
            budgetedQuantity: 100.0,
            previouslyConsumedQuantity: 0,
            totalConsumedQuantity: 85.0,
            totalRemainingQuantity: 15.0,
            currentConsumedQuantity: 85.0,
            currentRemainingQuantity: 15.0,
            assignedPercentage: 85.0,
            totalAmount: 238042.50
          }
        },

        // Machinery items for CIMENTACION
        {
          itemId: 5183,
          parentId: 1002,
          resourceId: 8800,
          resourceName: "1MAQU001",
          resourceDescription: "EXCAVADORA CAT 320D",
          resourceUnit: "HR",
          componentId: 615,
          componentName: "O002: CIMENTACION",
          componentType: "Machinery",
          quantity: 40,
          notes: "Excavadora para cimentación",
          productionUnitId: 73,
          productionUnitName: "Casa 101",
          componentResourceId: 9875,
          extendedItemData: {
            familyName: "Maquinaria",
            unit: "HR",
            currentInputQuantity: 40,
            currentInputPercentage: 20.0,
            unitCost: 350.00,
            budgetedQuantity: 200.0,
            previouslyConsumedQuantity: 80.0,
            totalConsumedQuantity: 120.0,
            totalRemainingQuantity: 80.0,
            currentConsumedQuantity: 40,
            currentRemainingQuantity: 40.0,
            assignedPercentage: 20.0,
            totalAmount: 14000.00
          }
        },
        {
          itemId: 5187,
          parentId: 1002,
          resourceId: 8801,
          resourceName: "1MAQU002",
          resourceDescription: "VIBRADOR DE CONCRETO",
          resourceUnit: "HR",
          componentId: 615,
          componentName: "O002: CIMENTACION",
          componentType: "Machinery",
          quantity: 24,
          notes: null,
          productionUnitId: 73,
          productionUnitName: "Casa 101",
          componentResourceId: 9881,
          extendedItemData: {
            familyName: "Maquinaria",
            unit: "HR",
            currentInputQuantity: 24,
            currentInputPercentage: 60.0,
            unitCost: 185.00,
            budgetedQuantity: 40.0,
            previouslyConsumedQuantity: 8.0,
            totalConsumedQuantity: 32.0,
            totalRemainingQuantity: 8.0,
            currentConsumedQuantity: 24,
            currentRemainingQuantity: 0,
            assignedPercentage: 60.0,
            totalAmount: 4440.00
          }
        },

        // Labor items for CIMENTACION
        {
          itemId: 5184,
          parentId: 1002,
          resourceId: 8811,
          resourceName: "1MANO001",
          resourceDescription: "ALBAÑIL ESPECIALIZADO",
          resourceUnit: "JOR",
          componentId: 615,
          componentName: "O002: CIMENTACION",
          componentType: "Labor",
          quantity: 24,
          notes: null,
          productionUnitId: 73,
          productionUnitName: "Casa 101",
          componentResourceId: 9876,
          extendedItemData: {
            familyName: "Mano de Obra",
            unit: "JOR",
            currentInputQuantity: 24,
            currentInputPercentage: 20.0,
            unitCost: 650.00,
            budgetedQuantity: 120.0,
            previouslyConsumedQuantity: 48.0,
            totalConsumedQuantity: 72.0,
            totalRemainingQuantity: 48.0,
            currentConsumedQuantity: 24,
            currentRemainingQuantity: 24.0,
            assignedPercentage: 20.0,
            totalAmount: 15600.00
          }
        },

        // Material items for ESTRUCTURA (child nodes)
        {
          itemId: 5188,
          parentId: 1003,
          resourceId: 8820,
          resourceName: "1ACER003",
          resourceDescription: "VARILLA CORRUGADA #5",
          resourceUnit: "TON",
          componentId: 616,
          componentName: "O003: ESTRUCTURA",
          componentType: "Material",
          quantity: 8.75,
          notes: "Varilla para refuerzo estructural",
          productionUnitId: 73,
          productionUnitName: "Casa 101",
          componentResourceId: 9890,
          extendedItemData: {
            familyName: "Acero",
            unit: "TON",
            currentInputQuantity: 8.75,
            currentInputPercentage: 35.0,
            unitCost: 1650.00,
            budgetedQuantity: 25.0,
            previouslyConsumedQuantity: 5.0,
            totalConsumedQuantity: 13.75,
            totalRemainingQuantity: 11.25,
            currentConsumedQuantity: 8.75,
            currentRemainingQuantity: 6.25,
            assignedPercentage: 35.0,
            totalAmount: 14437.50
          }
        },
        {
          itemId: 5189,
          parentId: 1003,
          resourceId: 8821,
          resourceName: "1PERF001",
          resourceDescription: "PERFIL IPR 12\"",
          resourceUnit: "ML",
          componentId: 616,
          componentName: "O003: ESTRUCTURA",
          componentType: "Material",
          quantity: 120.0,
          notes: null,
          productionUnitId: 73,
          productionUnitName: "Casa 101",
          componentResourceId: 9891,
          extendedItemData: {
            familyName: "Perfiles",
            unit: "ML",
            currentInputQuantity: 120.0,
            currentInputPercentage: 80.0,
            unitCost: 485.00,
            budgetedQuantity: 150.0,
            previouslyConsumedQuantity: 30.0,
            totalConsumedQuantity: 150.0,
            totalRemainingQuantity: 0,
            currentConsumedQuantity: 120.0,
            currentRemainingQuantity: 0,
            assignedPercentage: 80.0,
            totalAmount: 58200.00
          }
        },

        // Machinery for ESTRUCTURA
        {
          itemId: 5190,
          parentId: 1003,
          resourceId: 8822,
          resourceName: "1MAQU003",
          resourceDescription: "GRUA TELESCOPICA 25 TON",
          resourceUnit: "HR",
          componentId: 616,
          componentName: "O003: ESTRUCTURA",
          componentType: "Machinery",
          quantity: 16,
          notes: "Grúa para montaje de estructura",
          productionUnitId: 73,
          productionUnitName: "Casa 101",
          componentResourceId: 9892,
          extendedItemData: {
            familyName: "Maquinaria",
            unit: "HR",
            currentInputQuantity: 16,
            currentInputPercentage: 32.0,
            unitCost: 1250.00,
            budgetedQuantity: 50.0,
            previouslyConsumedQuantity: 10.0,
            totalConsumedQuantity: 26.0,
            totalRemainingQuantity: 24.0,
            currentConsumedQuantity: 16,
            currentRemainingQuantity: 14.0,
            assignedPercentage: 32.0,
            totalAmount: 20000.00
          }
        },

        // Labor for ESTRUCTURA
        {
          itemId: 5191,
          parentId: 1003,
          resourceId: 8823,
          resourceName: "1MANO003",
          resourceDescription: "SOLDADOR CERTIFICADO",
          resourceUnit: "JOR",
          componentId: 616,
          componentName: "O003: ESTRUCTURA",
          componentType: "Labor",
          quantity: 18,
          notes: null,
          productionUnitId: 73,
          productionUnitName: "Casa 101",
          componentResourceId: 9893,
          extendedItemData: {
            familyName: "Mano de Obra",
            unit: "JOR",
            currentInputQuantity: 18,
            currentInputPercentage: 30.0,
            unitCost: 850.00,
            budgetedQuantity: 60.0,
            previouslyConsumedQuantity: 12.0,
            totalConsumedQuantity: 30.0,
            totalRemainingQuantity: 30.0,
            currentConsumedQuantity: 18,
            currentRemainingQuantity: 12.0,
            assignedPercentage: 30.0,
            totalAmount: 15300.00
          }
        }
      ],
      Nodes: [] as any[] // Will be populated by transformation
    };

    // Transform the flat items structure into hierarchical nodes
    const transformedNodes = this.transformItemsToNodes(mockEstimationItemsExpanded.items);
    mockEstimationItemsExpanded.Nodes = transformedNodes;

    // Simulate API delay
    return of(mockEstimationItemsExpanded).pipe(
      map((response) => response),
      catchError((error) => {
        console.error('Error fetching expanded estimation items:', error);
        this.alertService.updateAlert(error.error!);
        this.alertService.changeErrorState(true);
        return of({ Nodes: [] });
      })
    );
  }

  /**
   * Transform flat items array into hierarchical nodes structure for tree view
   */
  private transformItemsToNodes(items: any[]): any[] {
    const nodes: any[] = [];
    
    // First pass: create all nodes
    items.forEach(item => {
      const node = {
        id: item.itemId,
        key: `item_${item.itemId}`,
        parentKey: item.parentId ? `item_${item.parentId}` : "",
        name: this.getDisplayName(item),
        type: item.componentType || 'Material',
        context: item.parentId ? 'EstimationItem' : 'EstimationComponent',
        notes: item.notes,
        WBSResourceNode: {
          unit: item.extendedItemData.unit || item.resourceUnit,
          unitPrice: item.extendedItemData.unitCost || 0,
          assignedQuantity: item.extendedItemData.currentInputQuantity || item.quantity || 0,
          assignedPercentage: item.extendedItemData.assignedPercentage || 0,
          totalAmount: item.extendedItemData.totalAmount || 0,
          budgetedQuantity: item.extendedItemData.budgetedQuantity || 0,
          previouslyConsumedQuantity: item.extendedItemData.previouslyConsumedQuantity || 0,
          totalConsumedQuantity: item.extendedItemData.totalConsumedQuantity || 0,
          totalRemainingQuantity: item.extendedItemData.totalRemainingQuantity || 0,
          currentInputPercentage: item.extendedItemData.currentInputPercentage || 0
        },
        // Additional properties from the real data structure
        resourceId: item.resourceId,
        resourceName: item.resourceName,
        resourceDescription: item.resourceDescription,
        componentId: item.componentId,
        componentName: item.componentName,
        productionUnitId: item.productionUnitId,
        productionUnitName: item.productionUnitName,
        componentResourceId: item.componentResourceId
      };
      
      nodes.push(node);
    });
    
    return nodes;
  }

  /**
   * Generate display name for tree nodes
   */
  private getDisplayName(item: any): string {
    if (item.componentType === 'Activity' || !item.parentId) {
      return item.componentName || item.resourceName;
    }
    
    const code = item.resourceName || '';
    const description = item.resourceDescription || '';
    return code && description ? `${code} - ${description}` : (code || description);
  }
}
